import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, Check, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { motion } from "framer-motion";
import SEO, { breadcrumbSchema } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  { id: "initial", title: "Initial Consultation", price: "$400" },
  { id: "rejuven", title: "Personalised Longevity & Wellness Programme", price: "From $3,500" },
  { id: "followup", title: "Follow-Up Consultation", price: "$250" },
  { id: "unsure", title: "Not sure yet — I'd like guidance", price: "" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] } },
};
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

export default function ApplyPage() {
  const [searchParams] = useSearchParams();
  const preselected = searchParams.get("service") || "";
  const [selectedService, setSelectedService] = useState(preselected);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !selectedService || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/applications`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service_interest: selectedService,
        message: form.message,
      });
      setSubmitted(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <main data-testid="apply-page" className="min-h-screen bg-[#FDFBF7]">
        <SEO
          title="Application Received"
          path="/apply"
          description="Thank you for applying for a consultation with Dr. Erika Tajra."
          noindex
        />
        <section className="pt-32 pb-24 md:pt-40 md:pb-36">
          <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
              <div className="w-16 h-16 mx-auto mb-8 bg-[#D4AF37] rounded-full flex items-center justify-center">
                <Check size={28} className="text-white" />
              </div>
              <h1 data-testid="apply-success-title" className="text-3xl md:text-4xl font-medium tracking-tight text-[#2D2D2D] mb-4">
                Application Received
              </h1>
              <p className="text-lg font-light text-[#6B6B6B] leading-relaxed mb-2">
                Thank you, {form.name}. Your application has been submitted.
              </p>
              <p className="text-base font-light text-[#6B6B6B] leading-relaxed mb-10">
                I'll review your details and be in touch within 48 hours.
              </p>
              <Link to="/" className="text-sm font-medium text-[#D4AF37] hover:text-[#2D2D2D] transition-colors underline underline-offset-4">
                Back to Home
              </Link>
            </motion.div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main data-testid="apply-page" className="min-h-screen bg-[#FDFBF7]">
      <SEO
        title="Apply for a Consultation"
        path="/apply"
        description="Apply for a personalised health consultation with Dr. Erika Tajra. Share what you're looking for help with and we'll be in touch."
        jsonLd={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Apply", path: "/apply" },
        ])}
      />

      {/* Hero */}
      <section className="pt-32 pb-12 md:pt-40 md:pb-16 bg-[#FDFBF7]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div variants={stagger} initial="hidden" animate="visible">
            <motion.p variants={fadeUp} className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-4">
              Apply
            </motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl font-medium tracking-tight leading-[1.1] text-[#2D2D2D] mb-6">
              Apply for a Consultation
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg font-light leading-relaxed text-[#6B6B6B] max-w-2xl">
              I work with a limited number of clients to ensure depth and quality.
              Tell me a little about yourself and what you're looking for — I'll be in touch within 48 hours.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="pb-24 md:pb-36">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <form onSubmit={handleSubmit} data-testid="apply-form" className="space-y-10">

            {/* Service Interest */}
            <div>
              <h2 className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-6">
                What are you interested in? *
              </h2>
              <div className="space-y-3">
                {SERVICES.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    data-testid={`apply-service-${s.id}`}
                    onClick={() => setSelectedService(s.id)}
                    className={`w-full text-left p-5 border transition-all duration-300 ${
                      selectedService === s.id
                        ? "border-[#D4AF37] bg-white shadow-[0_4px_24px_rgba(212,175,55,0.08)]"
                        : "border-[#F5F2EB] bg-white hover:border-[#D4AF37]/30"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-base font-medium text-[#2D2D2D]">{s.title}</span>
                      {s.price && (
                        <span className="text-sm text-[#D4AF37] font-medium">{s.price}</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Personal Details */}
            <div>
              <h2 className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-6">
                Your Details
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-medium tracking-widest uppercase text-[#6B6B6B] mb-2 block">Name *</label>
                  <Input
                    data-testid="apply-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="bg-white border-[#E6DCCA] h-12"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium tracking-widest uppercase text-[#6B6B6B] mb-2 block">Email *</label>
                  <Input
                    data-testid="apply-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-white border-[#E6DCCA] h-12"
                    placeholder="your@email.com"
                  />
                </div>
              </div>
              <div className="mt-5">
                <label className="text-xs font-medium tracking-widest uppercase text-[#6B6B6B] mb-2 block">Phone (optional)</label>
                <Input
                  data-testid="apply-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="bg-white border-[#E6DCCA] h-12"
                  placeholder="Your phone number"
                />
              </div>
            </div>

            {/* Message */}
            <div>
              <h2 className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-6">
                What are you looking for help with? *
              </h2>
              <Textarea
                data-testid="apply-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="bg-white border-[#E6DCCA] min-h-[160px]"
                placeholder="Tell me about your health goals, concerns, or what brought you here..."
              />
            </div>

            {/* Disclaimer */}
            <p className="text-xs text-[#6B6B6B] leading-relaxed">
              By submitting this application, you acknowledge that Erika Tajra is a UK-qualified health consultant
              and does not diagnose, treat, or prescribe as a licensed physician in the United States.
              Services are for educational and informational purposes.
            </p>

            {/* Submit */}
            <div>
              <button
                type="submit"
                data-testid="apply-submit"
                disabled={submitting}
                className="btn-gold inline-flex items-center gap-2 disabled:opacity-50"
              >
                {submitting ? "Submitting..." : "Submit Application"} <Send size={14} />
              </button>
            </div>
          </form>
        </div>
      </section>
    </main>
  );
}
